'use strict';

import { flow, action, observable, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import { sprintf } from 'sprintf-js';

import moment from 'moment';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import feedbackMessagesStore from '@uw-it-sis/lib-react/lib/FeedbackMessagesStore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {isEmpty, guid} from '@uw-it-sis/lib-react/lib/Utils';
import events from '@uw-it-sis/lib-react/lib/Events';
import PreferredCampusStore from '@uw-it-sis/lib-react-myplan/lib/PreferredCampusStore';
import PreferredCampusEvents from '@uw-it-sis/lib-react-myplan/lib/PreferredCampusEvents';
import api from '../lib/Api';


/**
 * Store for CourseSearch. This class is responsible for gathering user input, performing the search, and emitting
 * a success action.
 */
class CourseSearchStore {

    // Data load state. This one is for the initial setup.
    state = States.initial;

    // Search state. This is for blocking the UI during searches.
    searchState = States.initial;

    // This is data that we want to persist.
    data;

    constructor() {
        makeObservable(this, {
            state: observable,
            searchState: observable,
            data: observable,
            mount: action,
            onUpdateCampus: action,
            toggleAdvancedSearchPanel: action,
            updateCourseSearchFormData: action,
            updateActiveFilters: action,
            clearSearch: action,
            showBrowseMethod: action,
            enableSearch: action,
            disableSearch: action,
            updateNewSearch: action
        });

        this.componentKey = "CourseSearch";
        this.analyticsStore = appStore.getAnalyticsStore();
        this.exceptionUtils = appStore.getExceptionUtils();

        /**
         * Search results data object.
         */
        this.allCourses = [];

        // whether a new search has been fired or we hit the back button
        this.newSearch = false;

        /**
         * Non-persistent data objects.
         */
        this.displayedSearchResultsCount = 0;
        this.searchDisabled = true;

        // Register the store for URL persistence.
        this.urlPersistenceStore = appStore.getUrlPersistenceStore();
        this.urlPersistenceStore.register(this.componentKey, this.defaultData);

        /*
         * This is necessary so that when getSectionFiltersObject is passed as a prop in CourseSearch the "this" within
         * the method is this class rather than CourseSearch. Javascript is a little quirky with "this".
         */
        this.getSectionFiltersObject = this.getSectionFiltersObject.bind(this);
        // Same here.
        this.showBrowseMethod = this.showBrowseMethod.bind(this);
    }

    /**
     * Clean up.
     */
    unmount() {
        events.remove(PreferredCampusEvents.updatePreferredCampus, (campus) => this.onUpdateCampus(campus));
    }

    /**
     * Initialize the store with data from the UI.
     */
    mount(campus, isSectionSearch, queryString) {

        // Mount PreferredCampusStore to check if the user has set a preferredCampus
        PreferredCampusStore.mount();
        // Register event listeners
        events.listen(PreferredCampusEvents.updatePreferredCampus, (campus) => this.onUpdateCampus(campus));

        // Look for data in URL persistence.
        const data = this.urlPersistenceStore.getPersistedData(this.componentKey);

        this.newSearch = false;
        if (data == null) {
            // If the component state state wasn't available from URL persistence.
            this.data = this.defaultData();
        } else {
            // Otherwise, use the persisted data.
            this.data = data;
            if (this.data.formData.queryString) {
                this.searchDisabled = false;
            }
        }

        /*
         * Work out the appropriate campus. An init param trumps other settings.
         */
        if (campus) {
            this.data.formData.campus = campus;
        } else {
            // Otherwise, see if it has already been set.
            if (! this.data.formData.campus) {
                // Set it to the preferred campus if available, though it can be null here if we couldn't figure it out
                // on the backend and a choice hasn't been made yet.
                const campus = PreferredCampusStore.campus;
                if (campus) {
                    this.data.formData.campus = campus;
                }
            }
        }

        // Again, an init param overrides whatever is in the formData.
        if (isSectionSearch !== undefined) {
            this.data.formData.isSectionSearch = isSectionSearch;
        }

        if (queryString) {
            this.data.formData.queryString = queryString;
            // since a query string is initially set, do not disable the search
            this.searchDisabled = false;
        }

        // Perform a search if the search flag is set.
        if (this.data.formData.queryString) {
            this.search();
        }

        // Push the current state up to browser history, stripping out any of the init params.
        this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data,
            ["qs", "campus", "isSectionSearch"]);

        // return this.state;
        this.state = States.done;
    }

    /**
     * This is the default component data. It will be used if no data is found in URL persistence. It's also used
     * to validate any data found in the URL.
     */
    defaultData() {
        /*
         * Since terms are dynamic the default object will get populated and/or reconcile with data from the
         * term service.
         */
        return ({
            formData: {
                campus: undefined,
                isSectionSearch: true,
                isInstructorSearch: false,
                queryString: "",
                days: {"1": false, "2": false, "3": false, "4": false, "5": false, "6": false},
                startEndTimes: [0, 960],
                includeNonOpenSections: true,
                includeRestrictedSections: true,
                includePrereqsRequired: true,
                includeTbaSections: true,
                showOnlyPceSections: false,
                showOnlyInPersonSections: false
            },
            showBrowse: false,
            hasSearched: false,
            discloseAdvancedSearch: false
        });
    }

    /**
     * This is a listener for updateCampus action in CourseActions
     * This updated the campus in the form and triggers a state change.
     */
    onUpdateCampus(campus) {
        /*
         * If the campus has changed, the search and its results won't make sense in that new context, so clear the
         * search.
         */
        this.clearSearch();
    }

    /**
     * Campus convenience getter.
     */
    getCampus() {
        return this.data.formData.campus;
    }

    /**
     * Creates and returns a section filters object based on the current state of the section search criteria widgets
     * via the formData object.
     *
     * @param isPceSection If true, the PCE filter will be false.
     *
     * @returns {*} A section filters object if filtering should be applied. Otherwise, null.
     */
    getSectionFiltersObject(isPceSection) {
        // Make sure isPceSection is a bonafide boolean
        if (! isPceSection) {
            isPceSection = false;
        } else {
            isPceSection = true;
        }

        if (this._areSectionsFiltered() || isPceSection) {
            let selectedDays = this._selectedDayParams();

            let startTime = moment.utc(0).add({hours: 6, minutes: 30})
                .add(this.data.formData.startEndTimes[0], 'minutes').format("HHmm");
            let endTime = moment.utc(0).add({hours: 6, minutes: 30})
                .add(this.data.formData.startEndTimes[1], 'minutes').format("HHmm");

            // Look for Term filters.
            let terms = [];
            if (this.data.formData['activeFilters'] && this.data.formData.activeFilters.terms) {
                terms = this.data.formData.activeFilters.terms;
            }
            
            let filterObject = {
                "queryString": this.data.formData.queryString,
                "selectedDays": selectedDays,
                "startTime": startTime,
                "endTime": endTime,
                "openOnly": ! this.data.formData.includeNonOpenSections,
                "noEnrollmentRestrictionsOnly": ! this.data.formData.includeRestrictedSections,
                "noTbaOnly": ! this.data.formData.includeTbaSections,
                "pceOnly": isPceSection,
                "instructorSearch": this.data.formData.isInstructorSearch,
                "terms": terms,
                "inPerson": this.data.formData.showOnlyInPersonSections
            };

            return filterObject;
        } else {
            return null;
        }
    }

    /**
     * This is used to build an array with selectedDays.
     */
    _selectedDayParams(){
        let selectedDays = [];
        for (let day in this.data.formData.days) {
            if (this.data.formData.days[day]) {
                selectedDays.push(day);
            }
        }
        return selectedDays;
    }

    toggleAdvancedSearchPanel() {
        this.discloseAdvancedSearch = ! this.discloseAdvancedSearch;
    }

    /**
     * Performs a course search.
     */
    search = flow(function* () {

        // Update the state to pending.
        this.searchState = States.pending;

        // Start an analytics timer event.
        const timerEventName = "Search-Time-Tracking-Load-Time";
        this.analyticsStore.startTimer(timerEventName, {});


        /*
         * Reset stuff ....
         */
        // Clear courses.
        this.allCourses = [];

        // Set the last query string based on the search type.
        this.lastQueryString = this.data.formData.queryString;

        // Set this flag to true once a search has been performed.
        this.data.hasSearched = true;

        // This is the message key used to set/clear error messages if something goes wrong with the search
        const operationName = "submitSearchRequest";

        const postBody = this._formatPostData();

        if (appStore.isDebug()) {
            console.log("CourseSearchStore: Starting search.");
        }

        try {
            this.allCourses = yield api.findCourses(JSON.stringify(postBody));

            if (appStore.isDebug()) {
                console.log("CourseSearchStore: Search complete.");
            }
            // Clear any lingering messages.
            feedbackMessagesStore.clearMessage(operationName);
            // Cap off the timer event.
            this.analyticsStore.endTimer(timerEventName);

            // Push the current state up to browser history.
            this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data);

            // Finish.
            this.searchState = States.done;
        } catch (error) {
            // Set an error message with an operation id so that it can be cleared later if necessary.
            this.exceptionUtils.handleException(error, operationName);
            this.searchState = States.error;
        }
    }.bind(this));

    /**
     * This function is used to build a search request template used in analytics which contains all the search request params
     * that are selected to perform a search.
     * @param postData
     * @returns {*}
     * @private
     */
    _buildSearchRequestTemplate(postData) {

        let analyticsTemplate;

        if (postData.sectionSearch === true) {
            analyticsTemplate =
                "queryString=%s campus=%s sectionSearch=true days=%s startTime=%s endTime=%s "
              + "includeNonOpenSections=%s includeRestrictedSections=%s showOnlyPceSections=%s "
              + "showOnlyInPersonSections=%s includePrereqsRequired=%s includeTbaSections=%s requestId=%s";
        } else {
            analyticsTemplate = "queryString=%s campus=%s sectionSearch=false requestId=%s";
        }

        let queryString = "";
        if (postData.queryString) {
            queryString = postData.queryString.split(" ").join("^");
        }
        
        if (postData.sectionSearch === true) {
            return sprintf(
                analyticsTemplate,
                queryString,
                postData.campus ? postData.campus : "None",
                postData.days ? (postData.days.length > 0 ? postData.days.join(",") : "All") : "None",
                postData.startTime ? postData.startTime : "None",
                postData.endTime ? postData.endTime : "None",
                postData.includeNonOpenSections !== undefined ? postData.includeNonOpenSections : "None",
                postData.includeRestrictedSections !== undefined ? postData.includeRestrictedSections : "None",
                postData.showOnlyPceSections !== undefined ? postData.showOnlyPceSections : "None",
                postData.showOnlyInPersonSections !== undefined ? postData.showOnlyInPersonSections : "None",
                postData.includePrereqsRequired !== undefined ? postData.includePrereqsRequired : "None",
                postData.includeTbaSections !== undefined ? postData.includeTbaSections : "None",
                postData.requestId
            );
        } else {
            return sprintf(analyticsTemplate,
                queryString, postData.campus ? postData.campus : "None", postData.requestId);
        }
    }

    /**
     * Creates the POST body.
     */
    _formatPostData() {
        let formData = this.data.formData;
        let postBody = {};

        postBody["username"] = appStore.getUserName();
        postBody["requestId"] = guid();

        postBody["sectionSearch"] = formData.isSectionSearch;
        postBody["instructorSearch"] = formData.isInstructorSearch;
        postBody["queryString"] = formData.queryString;

        /*
         * Set the level. This affects the ordering of the search results.
         */
        let level = "UNDERGRADUATE";
        if (appStore.isGradStudent()) {
            level = "GRADUATE";
        }
        postBody["consumerLevel"] = level;

        /*
         * Section search properties
         */
        postBody["campus"] = formData.campus;

        /*
         * Fill in section-related parameters if it's a section search.
         */
        if (this.isSectionSearch()) {
            /*
             * Days. Note: On the backend not providing a days param returns any day.
             */
            let days = [];
            for (let key in formData.days) {
                if (formData.days[key]) {
                    days.push(key);
                }
            }
            postBody["days"] = days;

            // Start / End time
            let startTime = moment.utc(0).add({hours: 6, minutes: 30})
                .add(formData.startEndTimes[0], 'minutes');
            let endTime = moment.utc(0).add({hours: 6, minutes: 30})
                .add(formData.startEndTimes[1], 'minutes');
            postBody["startTime"] = startTime.format("HHmm");
            postBody["endTime"] = endTime.format("HHmm");

            //  Section status
            if (!formData.includeNonOpenSections) {
                postBody["includeNonOpenSections"] = false;
            }

            //  Enrollment restrictions
            if (!formData.includeRestrictedSections) {
                postBody["includeRestrictedSections"] = false;
            }

            //  PCE
            if (formData.showOnlyPceSections) {
                postBody["showOnlyPceSections"] = true;
            }

            // In Person
            if (formData.showOnlyInPersonSections) {
                postBody["showOnlyInPersonSections"] = true;
            }

            //  Prereqs required
            if (!formData.includePrereqsRequired) {
                postBody["includePrereqsRequired"] = false;
            }

            //  TBA sections
            if (!formData.includeTbaSections) {
                postBody["includeTbaSections"] = false;
            }
        }

        return postBody;
    }

    /**
     * Allows inputs on the form to communicate their values as they change.
     */
    updateCourseSearchFormData(property, newValue) {
        if (property.indexOf("days") === 0) {
            let [p, item] = property.split(":");
            this.data.formData[p][item] = ! this.data.formData[p][item];
        } else {
            if (property.indexOf("isSectionSearch") === 0) {
                let booleanValue;
                if (newValue && typeof newValue === 'string') {
                    if (newValue.toLowerCase() === "true") booleanValue = true;
                    if (newValue.toLowerCase() === "false") booleanValue = false;
                }
                newValue = booleanValue;
            }

            //  Save the new value of the property.
            this.data.formData[property] = newValue;
        }

        // Push the current state up to browser history.
        this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data);
    }

    /**
     * Adds the given facet filter object to the formData object so that it can be passed along and applied to the
     * CDP sections data for example.
     */
    updateActiveFilters(filtersObj) {
        if (! isEmpty(filtersObj)) {
            this.data.formData['activeFilters'] = filtersObj;
        } else {
            if (! isEmpty(this.data.formData['activeFilters'])) {
                delete this.data.formData['activeFilters'];
            }
        }
    }

    /**
     * Completely resets the search page.
     */
    clearSearch() {
        this.data = this.defaultData();
        this.allCourses = [];
        this.displayedSearchResultsCount = 0;
        this.searchDisabled = true;

        // The default campus value is null, but AppStore should have a value by now (either from the session or a
        // campusChange event.
        let preferredCampus = PreferredCampusStore.campus;
        if (preferredCampus) {
            this.data.formData.campus = preferredCampus;
        }

        // Push the current state up to browser history.
        this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data);
    }

    /**
     * Turns showBrowse on.
     */
    showBrowseMethod() {
        this.data.showBrowse = true;
        // Push the current state up to browser history.
        this.urlPersistenceStore.pushComponentDataToUrl(this.componentKey, this.data);
    }

    enableSearch() {
        this.searchDisabled = false;
    }

    disableSearch() {
        this.searchDisabled = true;
    }

    updateNewSearch(newSearch) {
        this.newSearch = newSearch;
    }

    /**
     * Determine if this is a section search based on whether or not a term has been selected.
     */
    isSectionSearch() {
        return(this.data.formData.isSectionSearch);
    }

    /**
     * Getter for all search results.
     * @return {Array}
     */
    getSearchResults() {
        return this.allCourses;
    }

    /**
     * Determines if any section filtering is in play by comparing the default values to the state of the formData object.
     * @returns {boolean} True if there are differences. Otherwise, false.
     *
     * @private
     */
    _areSectionsFiltered() {
        let defaultFormData = this.defaultData().formData;
        let currentFormData = this.data.formData;

        //  The first check is for facet
        return (
            currentFormData.activeFilters && ! isEmpty(currentFormData.activeFilters) ||

            ! this._isEquivalent(defaultFormData.days,currentFormData.days) ||
            ! this._isEquivalent(defaultFormData.startEndTimes,currentFormData.startEndTimes) ||
            (defaultFormData.includeNonOpenSections !== currentFormData.includeNonOpenSections) ||
            (defaultFormData.showOnlyPceSections !== currentFormData.showOnlyPceSections) ||
            (defaultFormData.showOnlyInPersonSections !== currentFormData.showOnlyInPersonSections) ||
            (defaultFormData.includeRestrictedSections !== currentFormData.includeRestrictedSections) ||
            (defaultFormData.includePrereqsRequired !== currentFormData.includePrereqsRequired) ||
            (defaultFormData.includeTbaSections !== currentFormData.includeTbaSections ) ||
            // instructor search (from auto-suggest dropdown) is now a filter
            // Maybe this test should be added to the if statement at line 149 instead?
            currentFormData.isInstructorSearch);
    }

    /**
     * This function is used to check if two objects have the same properties and values.
     * @returns {boolean}    true if given objects are equal else false.
     */
    _isEquivalent(obj1, obj2) {
        // Create arrays of property names
        let obj1Props = Object.getOwnPropertyNames(obj1);
        let obj2Props = Object.getOwnPropertyNames(obj2);

        // If number of properties is different,
        // objects are not equivalent
        if (obj1Props.length !== obj2Props.length) {
            return false;
        }

        for (let i = 0; i < obj1Props.length; i++) {
            let propName = obj1Props[i];

            // If values of same property are not equal,
            // objects are not equivalent
            if (obj1[propName] !== obj2[propName]) {
                return false;
            }
        }

        // If we made it this far, objects
        // are considered equivalent
        return true;
    }
    
    /**
     * Returns the start end times array.
     */
    getStartEndTimes() {
        return this.data.formData.startEndTimes;
    }

    /**
     * Used by the query string text field.
     */
    getQueryString() {
        return this.data.formData.queryString;
    }

    getSearchResultsCount() {
        return this.allCourses.length;
    }

    getDisplayedSearchResultsCount() {
        return this.displayedSearchResultsCount;
    }
}

export default new CourseSearchStore();